import React, { useState } from 'react';
import { Requirement } from '../types';
import '../styles/RequirementEditor.css';

interface Props {
  requirements: Requirement[];
  onAdd: (req: Omit<Requirement, 'id' | 'status'>) => void;
  onUpdate: (id: string, updates: Partial<Requirement>) => void;
  onDelete: (id: string) => void;
}

const RequirementEditor: React.FC<Props> = ({
  requirements,
  onAdd,
  onUpdate,
  onDelete
}) => {
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    priority: 'should' as Requirement['priority']
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.title.trim()) return;

    if (editingId) {
      onUpdate(editingId, formData);
      setEditingId(null);
    } else {
      onAdd(formData);
    }

    setFormData({ title: '', description: '', priority: 'should' });
    setShowForm(false);
  };

  const handleEdit = (req: Requirement) => {
    setFormData({
      title: req.title,
      description: req.description,
      priority: req.priority
    });
    setEditingId(req.id);
    setShowForm(true);
  };

  const handleCancel = () => {
    setFormData({ title: '', description: '', priority: 'should' });
    setEditingId(null);
    setShowForm(false);
  };

  const getPriorityColor = (priority: Requirement['priority']): string => {
    switch (priority) {
      case 'must': return '#e74c3c';
      case 'should': return '#f39c12';
      case 'could': return '#3498db';
      case 'wont': return '#95a5a6';
      default: return '#95a5a6';
    }
  };

  const getPriorityLabel = (priority: Requirement['priority']): string => {
    switch (priority) {
      case 'must': return 'Must Have';
      case 'should': return 'Should Have';
      case 'could': return 'Could Have';
      case 'wont': return "Won't Have";
      default: return priority;
    }
  };

  const groupedRequirements = {
    must: requirements.filter(r => r.priority === 'must'),
    should: requirements.filter(r => r.priority === 'should'),
    could: requirements.filter(r => r.priority === 'could'),
    wont: requirements.filter(r => r.priority === 'wont')
  };

  return (
    <div className="requirement-editor">
      <div className="editor-header">
        <div>
          <h2>Requirements</h2>
          <p className="subtitle">Define what your app needs to do (MoSCoW method)</p>
        </div>
        <button
          className="btn-primary"
          onClick={() => setShowForm(true)}
          disabled={showForm}
        >
          + Add Requirement
        </button>
      </div>

      {showForm && (
        <form className="requirement-form" onSubmit={handleSubmit}>
          <div className="form-group">
            <label>Title *</label>
            <input
              type="text"
              value={formData.title}
              onChange={e => setFormData({ ...formData, title: e.target.value })}
              placeholder="e.g., User can upload files"
              autoFocus
            />
          </div>

          <div className="form-group">
            <label>Description</label>
            <textarea
              value={formData.description}
              onChange={e => setFormData({ ...formData, description: e.target.value })}
              placeholder="Detailed description of the requirement..."
              rows={3}
            />
          </div>

          <div className="form-group">
            <label>Priority</label>
            <div className="priority-buttons">
              {(['must', 'should', 'could', 'wont'] as Requirement['priority'][]).map(p => (
                <button
                  key={p}
                  type="button"
                  className={`priority-btn ${formData.priority === p ? 'selected' : ''}`}
                  style={{
                    borderColor: formData.priority === p ? getPriorityColor(p) : undefined,
                    backgroundColor: formData.priority === p ? getPriorityColor(p) : undefined
                  }}
                  onClick={() => setFormData({ ...formData, priority: p })}
                >
                  {getPriorityLabel(p)}
                </button>
              ))}
            </div>
          </div>

          <div className="form-actions">
            <button type="button" className="btn-secondary" onClick={handleCancel}>
              Cancel
            </button>
            <button type="submit" className="btn-primary">
              {editingId ? 'Update' : 'Add'} Requirement
            </button>
          </div>
        </form>
      )}

      <div className="requirements-list">
        {Object.entries(groupedRequirements).map(([priority, reqs]) => (
          reqs.length > 0 && (
            <div key={priority} className="priority-group">
              <h3 style={{ color: getPriorityColor(priority as Requirement['priority']) }}>
                {getPriorityLabel(priority as Requirement['priority'])} ({reqs.length})
              </h3>
              <div className="requirement-cards">
                {reqs.map(req => (
                  <div
                    key={req.id}
                    className="requirement-card"
                    style={{ borderLeftColor: getPriorityColor(req.priority) }}
                  >
                    <div className="requirement-content">
                      <h4>{req.title}</h4>
                      {req.description && <p>{req.description}</p>}
                    </div>
                    <div className="requirement-actions">
                      <button onClick={() => handleEdit(req)} title="Edit">
                        ✏️
                      </button>
                      <button onClick={() => onDelete(req.id)} title="Delete">
                        🗑️
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )
        ))}

        {requirements.length === 0 && !showForm && (
          <div className="empty-state">
            <p>No requirements defined yet.</p>
            <p>Click "Add Requirement" to start defining what your app should do.</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default RequirementEditor;
